import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../../utils/api';
import ListingCard from '../Listings/ListingCard';
import CreateSublease from './CreateSublease';
import MapView from '../../components/MapView';
import { useAuth } from '../../context/AuthContext';

export default function Subleases() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [subleases, setSubleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(location.state?.openCreate || false);
  const [filters, setFilters] = useState({ min_rent: '', max_rent: '', start_before: '', end_after: '', bedrooms: '', bathrooms: '', parking: '', max_distance: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [saved, setSaved] = useState(new Set());
  const [viewMode, setViewMode] = useState('list');

  useEffect(() => { fetchSubleases(); }, [filters, page]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchSubleases() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/subleases?${params}`);
      setSubleases(res.data.subleases);
      setTotalPages(res.data.pages);
    } catch {}
    setLoading(false);
  }

  async function toggleSave(id) {
    try {
      if (saved.has(id)) {
        await api.delete(`/subleases/${id}/save`);
        setSaved(prev => { const s = new Set(prev); s.delete(id); return s; });
      } else {
        await api.post(`/subleases/${id}/save`);
        setSaved(prev => new Set([...prev, id]));
      }
    } catch {}
  }

  const [editSublease, setEditSublease] = useState(null);
  const [showConnectBanner, setShowConnectBanner] = useState(false);

  async function handleDelete(id) {
    if (!window.confirm('Are you sure you want to remove this sublease?')) return;
    try {
      await api.delete(`/subleases/${id}`);
      fetchSubleases();
    } catch {}
  }

  if (showCreate || editSublease) {
    return <CreateSublease
      existing={editSublease}
      onClose={() => {
        const justPosted = showCreate && !editSublease;
        setShowCreate(false);
        setEditSublease(null);
        fetchSubleases();
        if (justPosted && !user?.stripe_onboarding_complete) {
          setShowConnectBanner(true);
        }
      }}
    />;
  }

  const isLandlord = user?.user_type === 'landlord';

  return (
    <div className="page-container">

      {/* ── Stripe Connect reminder ── */}
      {showConnectBanner && (
        <div className="flex items-start gap-4 bg-[#FDF8E8] border border-[#C8A800]/30 rounded-xl p-4 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#C8A800]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="#C8A800" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#1B1B1B]">Your sublease is live — now set up payments</p>
            <p className="text-sm text-[#666666] mt-0.5">Connect your bank account so the new tenant can pay you directly through NestHawk. Takes about 2 minutes.</p>
            <Link to="/payments?tab=connect" className="inline-block mt-3 btn-primary text-sm px-4 py-2">
              Connect Bank Account →
            </Link>
          </div>
          <button onClick={() => setShowConnectBanner(false)} className="text-[#AAAAAA] hover:text-[#555555] transition-colors flex-shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="w-4 h-4">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {/* ── Page header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-[#EBEBEB]">
        <div>
          <h1 className="section-title">Sublease Listings</h1>
          <p className="text-sm text-[#999999] nav-link mt-1">Short-term sublets from students near campus</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          {/* List / Map toggle */}
          <div className="flex items-center gap-1 bg-[#F0F0F0] p-1 rounded-lg">
            {['list', 'map'].map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors nav-link ${viewMode === mode ? 'bg-white text-[#1B1B1B] shadow-sm' : 'text-[#777777] hover:text-[#1B1B1B]'}`}
              >
                {mode === 'list'
                  ? <span className="flex items-center gap-1.5"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>List</span>
                  : <span className="flex items-center gap-1.5"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>Map</span>
                }
              </button>
            ))}
          </div>
          <button
            onClick={() => navigate('/listings')}
            className="btn-outline flex items-center gap-2"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Post an Apartment
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="btn-primary flex items-center gap-2"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Post a Sublease
          </button>
        </div>
      </div>

      {/* ── Filters — hidden in map mode ── */}
      {viewMode === 'list' && (
        <div className="card p-4 mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="label text-xs">Min Rent</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA] text-sm">$</span>
                <input type="number" className="input pl-6 py-2 text-sm" placeholder="0" value={filters.min_rent}
                  onChange={e => { setFilters({ ...filters, min_rent: e.target.value }); setPage(1); }} />
              </div>
            </div>
            <div>
              <label className="label text-xs">Max Rent</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA] text-sm">$</span>
                <input type="number" className="input pl-6 py-2 text-sm" placeholder="Any" value={filters.max_rent}
                  onChange={e => { setFilters({ ...filters, max_rent: e.target.value }); setPage(1); }} />
              </div>
            </div>
            <div>
              <label className="label text-xs">Bedrooms</label>
              <select className="input py-2 text-sm" value={filters.bedrooms}
                onChange={e => { setFilters({ ...filters, bedrooms: e.target.value }); setPage(1); }}>
                <option value="">Any</option>
                {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} BR</option>)}
              </select>
            </div>
            <div>
              <label className="label text-xs">Bathrooms</label>
              <select className="input py-2 text-sm" value={filters.bathrooms}
                onChange={e => { setFilters({ ...filters, bathrooms: e.target.value }); setPage(1); }}>
                <option value="">Any</option>
                <option value="1">1+</option>
                <option value="1.5">1.5+</option>
                <option value="2">2+</option>
                <option value="3">3+</option>
              </select>
            </div>
            <div>
              <label className="label text-xs">Distance to Campus</label>
              <select className="input py-2 text-sm" value={filters.max_distance}
                onChange={e => { setFilters({ ...filters, max_distance: e.target.value }); setPage(1); }}>
                <option value="">Any</option>
                <option value="0.25">Under ¼ mile</option>
                <option value="0.5">Under ½ mile</option>
                <option value="1">Under 1 mile</option>
                <option value="2">Under 2 miles</option>
              </select>
            </div>
            <div>
              <label className="label text-xs">Parking</label>
              <select className="input py-2 text-sm" value={filters.parking}
                onChange={e => { setFilters({ ...filters, parking: e.target.value }); setPage(1); }}>
                <option value="">Any</option>
                <option value="reserved">Reserved Space</option>
                <option value="driveway">Driveway</option>
                <option value="street">Street Parking</option>
                <option value="paid">Parking Costs Extra</option>
                <option value="none">No Parking</option>
              </select>
            </div>
            <div>
              <label className="label text-xs">Available Before</label>
              <input type="date" className="input py-2 text-sm" value={filters.start_before}
                onChange={e => { setFilters({ ...filters, start_before: e.target.value }); setPage(1); }} />
            </div>
            <div>
              {Object.values(filters).some(v => v !== '') ? (
                <div className="flex items-end h-full pb-0.5">
                  <button
                    onClick={() => { setFilters({ min_rent: '', max_rent: '', start_before: '', end_after: '', bedrooms: '', bathrooms: '', parking: '', max_distance: '' }); setPage(1); }}
                    className="w-full py-2 text-sm text-[#999999] hover:text-[#555555] nav-link transition-colors border border-[#E8E8E8] rounded-lg"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <div>
                  <label className="label text-xs">Ends After</label>
                  <input type="date" className="input py-2 text-sm" value={filters.end_after}
                    onChange={e => { setFilters({ ...filters, end_after: e.target.value }); setPage(1); }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {viewMode === 'map' ? (
        <MapView
          listings={subleases}
          pinType="sublease"
          onViewListing={(s) => navigate(`/subleases/${s.id}`)}
        />
      ) : loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : subleases.length === 0 ? (
        <div className="text-center py-20">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-12 h-12 text-[#DDDDDD] mb-4 mx-auto">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
          </svg>
          <p className="text-xl font-bold text-[#1B1B1B] mb-2">No subleases found</p>
          <p className="nav-link text-[#777777] mb-6">Be the first to post one!</p>
          {!isLandlord && (
            <button onClick={() => setShowCreate(true)} className="btn-primary">
              Post a Sublease
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subleases.map(s => (
              <ListingCard key={s.id} listing={s} saved={saved.has(s.id)} onSave={() => toggleSave(s.id)} type="sublease" onEdit={() => setEditSublease(s)} onDelete={() => handleDelete(s.id)} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-ghost border border-[#DDDDDD] disabled:opacity-40">← Prev</button>
              <span className="flex items-center px-4 nav-link text-[#777777] text-sm">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-ghost border border-[#DDDDDD] disabled:opacity-40">Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
